import { safeReturnTo } from '@tms/shared-auth';
import { getApiUrl } from '../config/api';
import { logAuth } from './authDebug';
import { DEFAULT_UI_LANGUAGE, normalizeUiLanguage } from '../i18n/language';

export type PostAuthNavigate = (path: string, options?: { replace?: boolean }) => void;

/** Same redirect rules as login: admin surface selector or app/admin origin + returnTo. */
export async function redirectAfterAuth(
  navigate: PostAuthNavigate,
  searchParams?: URLSearchParams,
): Promise<void> {
  const params = searchParams ?? new URLSearchParams(window.location.search);
  const returnTo = safeReturnTo(params.get('returnTo')) || '/';
  const surfaceParam = params.get('surface');
  logAuth('post-auth navigation', { returnTo, surfaceParam });

  const langMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
  const routeLang = normalizeUiLanguage(langMatch?.[1] || DEFAULT_UI_LANGUAGE);

  if (surfaceParam !== 'admin') {
    try {
      const meRes = await fetch(getApiUrl('/api/v1/auth/me'), { credentials: 'include' });
      const meJson = await meRes.json().catch(() => ({}));
      const role = meJson?.data?.role;
      logAuth('post-auth /auth/me', {
        ok: meRes.ok,
        status: meRes.status,
        success: meJson?.success,
        role,
      });
      if (role === 'Admin') {
        const returnToQuery = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
        navigate(`/${routeLang}/select${returnToQuery}`, { replace: true });
        return;
      }
    } catch {
      // fallback to default surface
    }
  }

  const surface = surfaceParam === 'admin' ? 'admin' : 'app';
  const appOrigin = import.meta.env.VITE_APP_ORIGIN || 'http://app.localtest.me:3001';
  const adminOrigin = import.meta.env.VITE_ADMIN_ORIGIN || 'http://admin.localtest.me:3002';
  const targetOrigin = surface === 'admin' ? adminOrigin : appOrigin;
  const targetUrl = `${targetOrigin}${returnTo.startsWith('/') ? returnTo : '/'}`;
  logAuth('redirecting to surface', { surface, targetOrigin, targetUrl });
  window.location.assign(targetUrl);
}
